import Checkbox from '../../../common/components/Checkbox.js'
import {useSelector} from '../../../store.js'
import {
  isLabelPropertyEnabledSelector,
  onLabelPropertyChange,
} from '../../../data/labelInfos/index.js'
import {
  ENABLE_MODE__DISABLE,
  useLabelConfigContext,
} from '../LabelConfigContext.js'
import ButtonLink from '../../../common/components/Button/ButtonLink.js'
import Icon from '../../../common/icons/Icon.js'
import OverrideLabelProperty from './OverrideLabelProperty.js'

/**
 * Component to Enable/Disable properties for presets with just summary label
 *
 *
 * @param {Object} props
 * @param {String} label A string to display for this component
 * @param {String} props.labelProperty A property to enable
 * @param {Function} [props.checkedSelector] A function to determine if the checked state is true/false
 * @param {String} [props.overrideLabel] A label to describe what is getting overridden
 * @param {Object} [props.propertySpecs]
 * @param {Function} [props.labelPropertyCheckedSelector] A function to determine if the preset property is enabled
 * @param {String} [props.helpLink] A link to help
 * @param {String} [props.helpTitle] Help text
 * @returns {React.ReactNode}
 */
export function PickLabelProperty({
  label,
  labelProperty,
  checkedSelector,
  overrideLabel,
  propertySpecs,
  labelPropertyCheckedSelector,
  overrideHelpLink,
  overrideHelpTitle,
}) {
  const {labelInfoID} = useLabelConfigContext()

  return (
    <div className="list__item--shipping-options">
      <label htmlFor={`enable__${labelInfoID}__${labelProperty}`}>
        <strong>{label}</strong>
      </label>
      <EnableLabelProperty
        labelProperty={labelProperty}
        checkedSelector={checkedSelector}
      />
      {overrideLabel && (
        <OverrideLabelProperty
          propertySpecs={propertySpecs}
          label={overrideLabel}
          labelPropertyCheckedSelector={labelPropertyCheckedSelector}
          helpLink={overrideHelpLink}
          helpTitle={overrideHelpTitle}
        />
      )}
    </div>
  )
}

/**
 * Component to Enable/Disable properties for presets
 *
 * Put it in all label config properties so that we can reuse the label config form in a preset context
 *
 * @param {Object} props
 * @param {String} [props.labelProperty] A property to enable
 * @param {Function} [props.checkedSelector] A function to determine if the checked state is true/false
 * @param {Boolean} [props.overrideEnableMode] Enable mode override when you want to be different
 * @returns {React.ReactNode}
 */
export default function EnableLabelProperty({
  labelProperty,
  checkedSelector,
  overrideEnableMode,
}) {
  const context = useLabelConfigContext()
  let {labelInfoID, isLabelEnableMode, enableMode} = context

  if (overrideEnableMode) {
    enableMode = overrideEnableMode
  }
  const checked = useSelector(
    checkedSelector ||
      ((state) =>
        isLabelPropertyEnabledSelector(state, {
          labelProperty,
          labelInfoID,
        })),
  )

  if (!labelProperty || !isLabelEnableMode) {
    return null
  }

  if (enableMode === ENABLE_MODE__DISABLE) {
    return (
      <div className="wrap--edit-preset-remove">
        <ButtonLink
          id={`remove__${labelInfoID}__${labelProperty}`}
          className="no-underline"
          title="Remove this property from the preset"
          onClick={() => onLabelPropertyChange(context, labelProperty, false)}
        >
          <Icon name="trash" className="fs-01" />
        </ButtonLink>
      </div>
    )
  }

  return (
    <Checkbox
      id={`enable__${labelInfoID}__${labelProperty}`}
      mode="switch"
      checked={checked}
      onChange={(checked) =>
        onLabelPropertyChange(context, labelProperty, checked)
      }
    />
  )
}
